import React, { Suspense, useCallback, useMemo } from 'react';
import {
  BottomSheetContainer,
  createUseBottomSheetModal,
  Flex,
  RemoteImage,
  ScrollView,
  Spinner,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { LoadingWebView } from '../LoadingWebView';
import { isNil, isNotNil } from '../../utils';
import { useOpenDeeplink } from '../../hooks/useOpenDeeplink';
import { useLogAnalyticsEvent } from '../../hooks/useLogAnalyticsEvent';
import { useOverviewQuery } from './queries/useOverviewQuery';
import type { GetOverviewResponse } from '../../api';
import { useWebViewActionFromMessage } from '../../hooks';

/**
 * 시트 최대 높이 비율. 딥링크가 `height_ratio` 로 더 큰 값을 요구해도 여기서 잘린다
 * (`height_ratio` 가 없을 때의 기본값도 이 값).
 */
const MAX_HEIGHT_RATIO = 0.5;

/** 네이티브 뷰로 그릴 수 있는 오버뷰 카테고리(= `/webview/overview/{mode}` API 가 있는 것) */
const TITLE_MAP = {
  beauty: '뷰티 카테고리',
  shoppingmall: '쇼핑몰 카테고리',
  brand_department: '브랜드 카테고리',
};

type OverviewMode = keyof typeof TITLE_MAP;

type OverviewItem = GetOverviewResponse['icon_list'][number];

const isOverviewMode = (value: string): value is OverviewMode =>
  value in TITLE_MAP;

/** URL 이 `/overview/{mode}` 형태면 그 mode. 아니면 undefined(→ 웹뷰로 그린다) */
const getOverviewMode = (url: string): OverviewMode | undefined => {
  const match = url.match(/\/overview\/([^&?/]+)/);
  const mode = match?.[1];
  return isNotNil(mode) && isOverviewMode(mode) ? mode : undefined;
};

const CategoryView: React.FC<{ mode: OverviewMode }> = props => {
  const { data: overviewQuery } = useOverviewQuery(props.mode);
  const { width: screenWidth } = useScreenSize();
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const itemWidth = useMemo(() => {
    return (screenWidth - 16 * 2) / 5;
  }, [screenWidth]);

  const onPressItem = useCallback(
    (item: OverviewItem) => {
      logAnalyticsEvent({
        name: 'CLICK_CATEGORY',
        analytics: item.logging?.analytics,
        ably_only_log: item.logging?.ably_only_log,
      });

      openDeeplink(item.deeplink);
    },
    [openDeeplink, logAnalyticsEvent],
  );

  return (
    <ScrollView>
      <Flex.Row
        flexWrap={'wrap'}
        rowGap={16}
        padding={{ horizontal: 16, vertical: 12 }}
      >
        {overviewQuery.icon_list.map(item => (
          <Flex.Column
            key={item.sno}
            gap={4}
            alignItems={'center'}
            width={itemWidth}
            onPress={() => onPressItem(item)}
          >
            <RemoteImage
              uri={item.image_url}
              width={48}
              height={48}
              borderRadius={'xlarge'}
            />
            <Typography
              type={'subtitle4'}
              numberOfLines={1}
              textAlign={'center'}
            >
              {item.name}
            </Typography>
          </Flex.Column>
        ))}
      </Flex.Row>
    </ScrollView>
  );
};

/**
 * `ably://webview-bottomsheet` (및 `WEBVIEW_BOTTOM_SHEET_PATHS` 경로) 딥링크를 띄우는 시트.
 *
 * 내용은 두 갈래다.
 * - `/overview/{mode}` 로 네이티브 대체 뷰가 있는 카테고리: `CategoryView` 로 직접 그린다.
 * - 그 외(예: `/place/overview`): 대응 API 가 없으므로 웹뷰로 그 페이지를 띄운다.
 *
 * 높이는 `minHeight`/`maxHeight` 를 같은 값으로 줘서 시트 높이를 확정한다. 그래야 내부
 * 콘텐츠가 `flex={1}` 로 남은 공간(타이틀/세이프에어리어 제외)을 정확히 채우고, 웹뷰/
 * 스크롤뷰가 자기 높이를 알 수 있다.
 */
export const useWebviewBottomSheet = createUseBottomSheetModal<
  void,
  { url: string; heightRatio?: number | null }
>(({ param }) => {
  const { height: screenHeight } = useScreenSize();

  const mode = useMemo(() => getOverviewMode(param.url), [param.url]);
  const title = useMemo(() => (mode ? TITLE_MAP[mode] : undefined), [mode]);

  const sheetHeight = useMemo(() => {
    const ratio = isNil(param.heightRatio)
      ? MAX_HEIGHT_RATIO
      : Math.min(MAX_HEIGHT_RATIO, param.heightRatio / 100);
    return screenHeight * ratio;
  }, [param.heightRatio, screenHeight]);

  const webAction = useWebViewActionFromMessage();

  return (
    <BottomSheetContainer
      title={title}
      minHeight={sheetHeight}
      maxHeight={sheetHeight}
      showHandleBar
      hideBorder
    >
      <Flex flex={1}>
        {isNotNil(mode) ? (
          <Suspense
            fallback={
              <Flex.Center flex={1}>
                <Spinner />
              </Flex.Center>
            }
          >
            <CategoryView mode={mode} />
          </Suspense>
        ) : (
          // key: URL 이 바뀌면 로딩 상태를 처음부터 다시 시작한다(시트 자체는 계속 마운트)
          <LoadingWebView
            key={param.url}
            uri={param.url}
            onMessage={webAction}
          />
        )}
      </Flex>
    </BottomSheetContainer>
  );
});
